import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCreateItemsOptions,
  BulkCreateItemsResponse,
  BulkDeleteItemsResponse,
  BulkUpdateItemOptions,
  BulkUpdateItemResponse,
  CountItemsApplicationErrors,
  CountItemsOptions,
  CountItemsResponse,
  DuplicateItemOptions,
  DuplicateItemResponse,
  Item,
  ItemCreatedEnvelope,
  ItemDeletedEnvelope,
  ItemQuery,
  ItemSearch,
  ItemUpdatedEnvelope,
  ItemsQueryBuilder,
  ListItemsOptions,
  ListItemsResponse,
  MaskedItem,
  QueryItemsResponse,
  SearchItemsResponse,
  UpdateItem,
  bulkCreateItems as universalBulkCreateItems,
  bulkDeleteItems as universalBulkDeleteItems,
  bulkUpdateItem as universalBulkUpdateItem,
  countItems as universalCountItems,
  createItem as universalCreateItem,
  deleteItem as universalDeleteItem,
  duplicateItem as universalDuplicateItem,
  getItem as universalGetItem,
  listItems as universalListItems,
  queryItems as universalQueryItems,
  searchItems as universalSearchItems,
  typedQueryItems as universalTypedQueryItems,
  updateItem as universalUpdateItem,
} from './restaurants-menus-v1-item-items.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/restaurants' };

export function duplicateItem(httpClient: HttpClient): DuplicateItemSignature {
  return (_id: string, options?: DuplicateItemOptions) =>
    universalDuplicateItem(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DuplicateItemSignature {
  /** @param - Item id to be duplicated. */
  (_id: string, options?: DuplicateItemOptions): Promise<
    NonNullablePaths<DuplicateItemResponse, `itemId`, 2>
  >;
}

export function createItem(httpClient: HttpClient): CreateItemSignature {
  return (item: NonNullablePaths<Item, `modifierGroups.${number}._id`, 4>) =>
    universalCreateItem(
      item,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateItemSignature {
  /**
   * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Creates an item.
   *
   * To create multiple items at once, use [Bulk Create Items](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/item/bulk-create-items).
   * @param - Item details.
   * @returns Item.
   */
  (item: NonNullablePaths<Item, `modifierGroups.${number}._id`, 4>): Promise<
    NonNullablePaths<
      Item,
      | `priceVariants.variants`
      | `priceVariants.variants.${number}.priceInfo.price`
      | `name`
      | `additionalImages`
      | `labels`
      | `labels.${number}._id`
      | `modifierGroups`
      | `businessLocationIds`,
      6
    >
  >;
}

export function bulkCreateItems(
  httpClient: HttpClient
): BulkCreateItemsSignature {
  return (
    items: NonNullablePaths<Item, `modifierGroups.${number}._id`, 4>[],
    options?: BulkCreateItemsOptions
  ) =>
    universalBulkCreateItems(
      items,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateItemsSignature {
  /**
   * > **Note:** The Items API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Creates multiple items at once.
   * @param - Items details.
   */
  (
    items: NonNullablePaths<Item, `modifierGroups.${number}._id`, 4>[],
    options?: BulkCreateItemsOptions
  ): Promise<
    NonNullablePaths<
      BulkCreateItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.priceInfo.price`
      | `results.${number}.item.name`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function getItem(httpClient: HttpClient): GetItemSignature {
  return (itemId: string) =>
    universalGetItem(
      itemId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetItemSignature {
  /**
   * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Retrieves an item by ID.
   * @param - Item ID.
   * @returns Item.
   */
  (itemId: string): Promise<
    NonNullablePaths<
      Item,
      | `priceVariants.variants`
      | `priceVariants.variants.${number}.priceInfo.price`
      | `name`
      | `additionalImages`
      | `labels`
      | `labels.${number}._id`
      | `modifierGroups`
      | `businessLocationIds`,
      6
    >
  >;
}

export function listItems(httpClient: HttpClient): ListItemsSignature {
  return (options?: ListItemsOptions) =>
    universalListItems(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListItemsSignature {
  /**
   * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Retrieves a list of up to 500 items.
   */
  (options?: ListItemsOptions): Promise<
    NonNullablePaths<
      ListItemsResponse,
      `items` | `items.${number}.priceInfo.price` | `items.${number}.name`,
      5
    >
  >;
}

export function queryItems(httpClient: HttpClient): QueryItemsSignature {
  return () =>
    universalQueryItems(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryItemsSignature {
  /**
   * Creates a query to retrieve a list of items.
   *
   * The `queryItems()` function builds a query to retrieve a list of items and returns a `ItemsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the [`find()`](/items/items-query-builder/find) function.
   *
   * You can refine the query by chaining `ItemsQueryBuilder` functions onto the query. `ItemsQueryBuilder` functions enable you to filter, sort, and control the results that `queryItems()` returns.
   *
   * `queryItems()` runs with the following `ItemsQueryBuilder` defaults, which you can override:
   *
   * * [`limit(500)`](/items/items-query-builder/limit)
   *
   * The following `ItemsQueryBuilder` functions are supported for `queryItems()`. For a full description of the item object, see the object returned for the [`items`](/items/items-query-result/items) property in `ItemsQueryResult`.
   */
  (): ItemsQueryBuilder;
}

export function typedQueryItems(
  httpClient: HttpClient
): TypedQueryItemsSignature {
  return (query: ItemQuery) =>
    universalTypedQueryItems(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryItemsSignature {
  /** */
  (query: ItemQuery): Promise<
    NonNullablePaths<
      QueryItemsResponse,
      `items` | `items.${number}.priceInfo.price` | `items.${number}.name`,
      5
    >
  >;
}

export function searchItems(httpClient: HttpClient): SearchItemsSignature {
  return (search: ItemSearch) =>
    universalSearchItems(
      search,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchItemsSignature {
  /**
   * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Retrieves a list of items given the provided filtering, paging, and sorting.
   *
   * ## Defaults
   * Search Items has the following default settings, which you can override:
   *
   * - Sorted by `createdDate` in ascending order.
   * - `paging.limit` set to `500`.
   * - `paging.offset` set to `0`.
   * @param - Search options.
   */
  (search: ItemSearch): Promise<
    NonNullablePaths<
      SearchItemsResponse,
      | `items`
      | `items.${number}.priceInfo.price`
      | `items.${number}.name`
      | `aggregationData.results`
      | `aggregationData.results.${number}.scalar.type`
      | `aggregationData.results.${number}.scalar.value`
      | `aggregationData.results.${number}.name`
      | `aggregationData.results.${number}.type`,
      6
    >
  >;
}

export function countItems(httpClient: HttpClient): CountItemsSignature {
  return (options?: CountItemsOptions) =>
    universalCountItems(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CountItemsSignature {
  /**
   * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Retrieves the number of items that match a specified filter.
   *
   * If a filter isn't passed in the request, the endpoint returns the count of all items.
   */
  (options?: CountItemsOptions): Promise<
    NonNullablePaths<CountItemsResponse, `count`, 2> & {
      __applicationErrorsType?: CountItemsApplicationErrors;
    }
  >;
}

export function updateItem(httpClient: HttpClient): UpdateItemSignature {
  return (
    _id: string,
    item: NonNullablePaths<
      UpdateItem,
      `modifierGroups.${number}._id` | `revision`,
      4
    >
  ) =>
    universalUpdateItem(
      _id,
      item,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateItemSignature {
  /**
   * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Updates an item.
   *
   * To update multiple items at once, use [Bulk Update Item](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/item/bulk-update-item)
   *
   * Each time an item is updated, its revision increments by 1. The existing revision must be included when updating the item. This ensures you're working with the latest item information, and it prevents unintended overwrites.
   * @param - Item ID.
   * @returns Updated item.
   */
  (
    _id: string,
    item: NonNullablePaths<
      UpdateItem,
      `modifierGroups.${number}._id` | `revision`,
      4
    >
  ): Promise<
    NonNullablePaths<
      Item,
      | `priceVariants.variants`
      | `priceVariants.variants.${number}.priceInfo.price`
      | `name`
      | `additionalImages`
      | `labels`
      | `labels.${number}._id`
      | `modifierGroups`
      | `businessLocationIds`,
      6
    >
  >;
}

export function bulkUpdateItem(
  httpClient: HttpClient
): BulkUpdateItemSignature {
  return (
    items: NonNullablePaths<
      MaskedItem,
      `item._id` | `item.modifierGroups.${number}._id` | `item.revision`,
      5
    >[],
    options?: BulkUpdateItemOptions
  ) =>
    universalBulkUpdateItem(
      items,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateItemSignature {
  /**
   * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Updates up to 100 multiple items at once.
   *
   * Each time an item is updated, its revision increments by 1. The existing revision must be included when updating item. This ensures you're working with the latest item information, and it prevents unintended overwrites.
   * @param - Items to update.
   */
  (
    items: NonNullablePaths<
      MaskedItem,
      `item._id` | `item.modifierGroups.${number}._id` | `item.revision`,
      5
    >[],
    options?: BulkUpdateItemOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateItemResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.priceInfo.price`
      | `results.${number}.item.name`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function deleteItem(httpClient: HttpClient): DeleteItemSignature {
  return (itemId: string) =>
    universalDeleteItem(
      itemId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteItemSignature {
  /**
   * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Deletes an item.
   * @param - Item ID.
   */
  (itemId: string): Promise<void>;
}

export function bulkDeleteItems(
  httpClient: HttpClient
): BulkDeleteItemsSignature {
  return (ids: string[]) =>
    universalBulkDeleteItems(
      ids,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteItemsSignature {
  /**
   * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Deletes multiple items at once.
   * @param - Item IDs.
   */
  (ids: string[]): Promise<
    NonNullablePaths<
      BulkDeleteItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export const onItemCreated = EventDefinition(
  'wix.restaurants.menus.v1.item_created',
  true,
  (event: ItemCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            {
              path: 'entity.orderSettings.availability.timestampRangesOptions.ranges.startTime',
            },
            {
              path: 'entity.orderSettings.availability.timestampRangesOptions.ranges.endTime',
            },
            { path: 'entity.externalReferenceInfo.lastSyncDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.image' },
            { path: 'entity.additionalImages', isRepeated: true },
          ],
        },
      ])
    )
)<ItemCreatedEnvelope>();
export const onItemDeleted = EventDefinition(
  'wix.restaurants.menus.v1.item_deleted',
  true,
  (event: ItemDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            {
              path: 'entity.orderSettings.availability.timestampRangesOptions.ranges.startTime',
            },
            {
              path: 'entity.orderSettings.availability.timestampRangesOptions.ranges.endTime',
            },
            { path: 'entity.externalReferenceInfo.lastSyncDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.image' },
            { path: 'entity.additionalImages', isRepeated: true },
          ],
        },
      ])
    )
)<ItemDeletedEnvelope>();
export const onItemUpdated = EventDefinition(
  'wix.restaurants.menus.v1.item_updated',
  true,
  (event: ItemUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            {
              path: 'entity.orderSettings.availability.timestampRangesOptions.ranges.startTime',
            },
            {
              path: 'entity.orderSettings.availability.timestampRangesOptions.ranges.endTime',
            },
            { path: 'entity.externalReferenceInfo.lastSyncDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            {
              path: 'modifiedFields.orderSettings.availability.timestampRangesOptions.ranges.startTime',
            },
            {
              path: 'modifiedFields.orderSettings.availability.timestampRangesOptions.ranges.endTime',
            },
            { path: 'modifiedFields.externalReferenceInfo.lastSyncDate' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.image' },
            { path: 'entity.additionalImages', isRepeated: true },
            { path: 'modifiedFields.image' },
            { path: 'modifiedFields.additionalImages', isRepeated: true },
          ],
        },
      ])
    )
)<ItemUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Address,
  AddressHint,
  Aggregation,
  AggregationData,
  AggregationKindOneOf,
  AggregationResults,
  AggregationResultsResultOneOf,
  AggregationType,
  App,
  ApplicationError,
  Availability,
  AvailabilityAvailabilityTypeOptionsOneOf,
  AvailabilityType,
  AvailableRange,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkCreateItemResult,
  BulkCreateItemsOptions,
  BulkCreateItemsRequest,
  BulkCreateItemsResponse,
  BulkDeleteItemResult,
  BulkDeleteItemsRequest,
  BulkDeleteItemsResponse,
  BulkItemResult,
  BulkUpdateBusinessLocationIdsPerEntityRequest,
  BulkUpdateBusinessLocationIdsPerEntityResponse,
  BulkUpdateBusinessLocationIdsRequest,
  BulkUpdateBusinessLocationIdsResponse,
  BulkUpdateBusinessLocationIdsResponseBulkItemResult,
  BulkUpdateItemOptions,
  BulkUpdateItemRequest,
  BulkUpdateItemResponse,
  BulkUpdateItemResponseBulkItemResult,
  BusinessSchedule,
  Categories,
  ChangeContext,
  ChangeContextPayloadOneOf,
  CloneItemsRequest,
  CloneItemsResponse,
  ConsentPolicy,
  CountItemsOptions,
  CountItemsRequest,
  CountItemsResponse,
  CreateItemRequest,
  CreateItemResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  CursorSearch,
  CursorSearchPagingMethodOneOf,
  Cursors,
  CustomTag,
  DayOfWeek,
  DayOfWeekAvailability,
  DeleteByFilterOperation,
  DeleteByIdsOperation,
  DeleteItemRequest,
  DeleteItemResponse,
  DeleteLocationIdsFromModifierGroups,
  DeleteLocationIdsFromVariants,
  DocumentImage,
  DocumentPayload,
  DocumentUpdateOperation,
  DomainEvent,
  DomainEventBodyOneOf,
  DuplicateItemOptions,
  DuplicateItemRequest,
  DuplicateItemResponse,
  Empty,
  EntitiesDayOfWeek,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  Enum,
  EventMetadata,
  ExtendedFields,
  ExternalReferenceInfo,
  File,
  FulfillmentMethodType,
  GeoCoordinates,
  GetItemRequest,
  GetItemResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  IndexDocument,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  Item,
  ItemCreatedEnvelope,
  ItemDeletedEnvelope,
  ItemIdLocationIds,
  ItemMetadata,
  ItemPricingOneOf,
  ItemQuerySpec,
  ItemSearchSpec,
  ItemUpdatedEnvelope,
  ItemsQueryBuilder,
  ItemsQueryResult,
  Label,
  ListItemsOptions,
  ListItemsRequest,
  ListItemsResponse,
  Locale,
  MaskedItem,
  MessageEnvelope,
  Mode,
  ModifierGroup,
  Multilingual,
  OrderSettings,
  Page,
  Pages,
  PlacementType,
  PriceInfo,
  PriceVariant,
  PriceVariants,
  Properties,
  PropertiesChange,
  QueryItemsRequest,
  QueryItemsResponse,
  RangeAggregation,
  RangeAggregationResult,
  RangeBucket,
  RangeResults,
  ResolutionMethod,
  RestoreInfo,
  ScalarAggregation,
  ScalarResult,
  ScalarType,
  SearchDetails,
  SearchIndexingNotification,
  SearchItemsRequest,
  SearchItemsResponse,
  SiteCloned,
  SiteCreated,
  SitePropertiesEvent,
  SitePropertiesNotification,
  SortDirection,
  SortOrder,
  SortType,
  Sorting,
  SpecialHourPeriod,
  State,
  SupportedLanguage,
  SyncContentToMultilingualRequest,
  SyncContentToMultilingualResponse,
  TimeOfDay,
  TimeOfDayRange,
  TimePeriod,
  TimestampRangesOptions,
  Translation,
  URI,
  URIs,
  UpdateByFilterOperation,
  UpdateDocumentsEvent,
  UpdateDocumentsEventOperationOneOf,
  UpdateExistingOperation,
  UpdateItem,
  UpdateItemRequest,
  UpdateItemResponse,
  ValueAggregation,
  ValueAggregationResult,
  ValueResults,
  WebhookIdentityType,
  WeeklyScheduleOptions,
  utils,
} from './restaurants-menus-v1-item-items.universal.js';
